// src/modules/components/inkind/LinkContributionModal.js
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
} from '@mui/material';
import {
    Form,
    ReferenceInput,
    AutocompleteInput,
    TextInput,
    required,
    useNotify,
} from 'react-admin';

const token = JSON.parse(localStorage.getItem('token'));

// resourceType: 'offers' | 'services' — matches the react-admin resource name used to search counterparts
const LinkContributionModal = ({ open, onClose, record, resourceType }) => {
    const notify = useNotify();
    const [saving, setSaving] = useState(false);

    if (!record) return null;

    const isService = resourceType === 'services';
    const itemType = isService ? record.service_type : record.offer_type;
    const isNeed = itemType === 'need';
    const counterpartType = isNeed ? 'donate' : 'need';

    const filter = isService
        ? { service_type: counterpartType, is_in_kind: true, status: 'active' }
        : { offer_type: counterpartType, is_in_kind: true, status: 'active' };

    const optionText = isService
        ? (r) => `${r.service?.name || 'Service'} (${r.user?.name || ''})`
        : (r) => `${r.offer || r.subCategory?.name || 'Offer'} (${r.user?.name || ''})`;

    const basePath = isService ? 'admin/in-kind-services' : 'admin/in-kind-offers';

    const handleSubmit = async (values) => {
        if (!values.counterpartId) {
            notify('Please select an item to link', { type: 'warning' });
            return;
        }

        const needId = isNeed ? record.id : values.counterpartId;
        const donationId = isNeed ? values.counterpartId : record.id;

        try {
            setSaving(true);
            const response = await fetch(
                `${process.env.REACT_APP_URL}/${basePath}/${needId}/link-donation/${donationId}`,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + token,
                    },
                    body: JSON.stringify({
                        allocated_quantity: values.allocated_quantity || undefined,
                        note: values.note || undefined,
                    }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || 'Failed to link contribution');
            }

            notify('Contribution linked successfully', { type: 'success' });
            onClose(true);
        } catch (error) {
            console.error('Error linking contribution:', error);
            notify(error.message || 'Failed to link contribution', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !saving && onClose(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Link {isNeed ? 'a Donation' : 'a Need'}</DialogTitle>
            <Form onSubmit={handleSubmit}>
                <DialogContent>
                    <ReferenceInput
                        source="counterpartId"
                        reference={resourceType}
                        filter={filter}
                        label={isNeed ? 'Select Donation' : 'Select Need'}
                        validate={[required()]}
                    >
                        <AutocompleteInput
                            optionText={optionText}
                            fullWidth
                            margin="normal"
                        />
                    </ReferenceInput>
                    <TextInput
                        source="allocated_quantity"
                        label="Allocated Quantity"
                        fullWidth
                        margin="normal"
                    />
                    <TextInput
                        source="note"
                        label="Note"
                        multiline
                        rows={2}
                        fullWidth
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => onClose(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {saving ? 'Linking...' : 'Link'}
                    </Button>
                </DialogActions>
            </Form>
        </Dialog>
    );
};

export default LinkContributionModal;
